import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectorRef, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { SortDirection } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { AuthService, AppInsightsService } from '@k12/app-services';
import { CommentsService } from '@k12/comments';
import { LoadingSpinnerService } from '@k12/loading';
import {
  Comment,
  IssueHistory,
  CommentType,
  SeverityLevel,
  ProviderInfoStatus,
  TaskInfoStatus,
  SvgIcon,
  ToastMessageType,
  UserRecordType,
  EntraUserGroup
} from '@k12/models';
import { ToastMessageService } from '@k12/notifications';
import { TruncatePipe } from '@k12/pipes';
import { CfiMatTableComponent, ColumnDefinition, ExpandableRow } from '@k12/tables';
import { StatusBadgeComponent, StatusBadgeSize, SvgIconComponent, CommentComponent } from '@k12/ui-primitives';
import { ContactsComponent } from '@k12/user-info';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

const redStatuses = [
  ProviderInfoStatus.UnderReview,
];

const greenStatuses = [
  ProviderInfoStatus.Approved,
  ProviderInfoStatus.Active,
];

const taskRedStatuses = new Set<TaskInfoStatus>([TaskInfoStatus.PastDue]);

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-user-record-table',
  imports: [
    CfiMatTableComponent,
    CommonModule,
    CommentComponent,
    ContactsComponent,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    StatusBadgeComponent,
    SvgIconComponent,
    TruncatePipe
  ],
  templateUrl: './user-record-table.component.html',
  styleUrls: ['./user-record-table.component.scss']
})
export class UserRecordTableComponent<T = object> implements OnInit, OnChanges, OnDestroy {
  @Input() columnDefinitions: ColumnDefinition[] = [];
  @Input() data: T[] = [];
  @Input() expandable: boolean = false;
  @Input() label: string = 'More';
  @Input() maxInitialRecords: number = 4;
  @Input() paginated: boolean = false;
  @Input() pageSize: number = 5;
  @Input() pageSizeOptions: number[] = [5, 10, 25];
  @Input() scrollable: boolean = false;
  @Input() serverSidePaging: boolean = false;
  @Input() showStatusBadge: boolean = false;
  @Input() sortKey: string = '';
  @Input() subCellsKey: string = 'subCells';
  @Input() title: string = 'User Record Table';
  @Input() showViewMore: boolean = true;

  /** Server-side total row count (required when serverSidePaging is true). */
  @Input() totalRows: number = 0;

  /** Zero-based page index (server-side). */
  @Input() currentPageIndex: number = 0;
  @Input() hidePageSizeSelector: boolean = false;
  @Input() highlightActiveRows: boolean = false;
  @Input() recordType: UserRecordType | null = null;
  @Input() sortDirection: SortDirection = 'asc';
  @Input() userTypeId: number | null = null;

  // Badge configuration inputs
  @Input() badgeMessage: string = 'Under Review';
  @Input() customBadgeCount: number | null = null;

  // Button configuration inputs
  @Input() buttonDisabled: boolean = false;
  @Input() buttonText: string = 'Action';
  @Input() showButton: boolean = false;

  // Icon configuration inputs
  @Input() iconName: string = 'add';
  @Input() isSvgIcon: boolean = false;
  @Input() showIcon: boolean = false;
  @Input() svgIconName: SvgIcon;

  // For header only demo data
  @Input() demo: boolean = false;

  //Custom empty label input
  @Input() emptyLabel: string = '';

  // Output event for button click (alternative to function input)
  @Output() addNewButtonClick = new EventEmitter<void>();
  @Output() addNewCommentClick = new EventEmitter<{ action: string; comment?: Comment }>();
  @Output() cbSort = new EventEmitter<{ active: string; direction: SortDirection }>();
  @Output() editIssueClick = new EventEmitter<IssueHistory>();
  @Output() messageSent = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() studentClick = new EventEmitter<{ studentId: string; studentName: string }>();
  @Output() viewMoreClick = new EventEmitter<void>();
  @Output() scrolledNearEnd = new EventEmitter<void>();

  // View more functionality
  commentBody: string = '';
  commentType = CommentType;
  currentUserLocalAccountId: string = '';
  public dataVersion: number = 0; // Add a version counter for tracking changes
  displayedColumns: string[] = [];
  displayData: T[] = [];
  expandedElement: T | null = null;
  isAddComment: boolean = false; // Flag to show textbox for new comment
  isSuperAdmin: boolean = false; // Flag to indicate if user is a super admin
  isAdmin: boolean = false; // Flag to indicate if user is an admin
  programPartnerId: string | null = null;
  public tableUpdated: boolean = false; // Flag to track when table data changes
  UserRecordType = UserRecordType;

  readonly editIcon = SvgIcon.Edit;
  readonly greenStatuses = greenStatuses;
  readonly isNotPrimaryIcon = SvgIcon.isNotPrimary;
  readonly isPrimaryIcon = SvgIcon.isPrimary;
  readonly pendingStatus = ProviderInfoStatus.Pending;
  readonly ProviderInfoStatus = ProviderInfoStatus;
  readonly redStatuses = redStatuses;
  readonly statusBadgeSize = StatusBadgeSize.Small;
  readonly trashCanIcon = SvgIcon.TrashCan;
  readonly underReviewStatus = ProviderInfoStatus.UnderReview;

  private readonly authService = inject(AuthService);
  private readonly msalService = inject(MsalService);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly commentsService = inject(CommentsService);
  private readonly destroy$ = new Subject<void>();
  private readonly route = inject(ActivatedRoute);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);

  // Row highlight function for issue tracking and highlight flag for ordering
  private isActiveHighlightRow(row: T): boolean {
    if (this.recordType !== UserRecordType.IssueTracking) {
      return false;
    }
    const { certification, endorsement, payment } = this.getIssueTrackingOnHoldStatus(row);
    return certification || endorsement || payment;
  }

  readonly rowHighlightClassFn = (row: T): string => (this.isActiveHighlightRow(row) ? 'user-record-row--active' : '');

  readonly rowClassFn = (row: T): string => {
    if (this.recordType === UserRecordType.Tasks) {
      const status = (row as Record<string, unknown>)['taskStatus'] as string;
      if (status === TaskInfoStatus.PastDue) return 'task-row--past-due';
      if (status === TaskInfoStatus.Accepted) return 'task-row--accepted';
      return 'task-row--neutral';
    }
    return this.highlightActiveRows ? this.rowHighlightClassFn(row) : '';
  };

  getTableClasses(): string[] {
    const classes: string[] = [];
    if (this.recordType === UserRecordType.Students) {
      classes.push('student-table');
    }
    if (this.recordType === UserRecordType.IssueTracking) {
      classes.push('full-width-table');
    }
    return classes;
  }

  /** Issue tracking + highlightActiveRows: edit is only enabled for rows that count as "active" (highlighted). */
  isRowActionEditEnabled(row: T): boolean {
    // Disable edit action for sub-rows (issue tracking child rows)
    if (this.isSubRow(row) && this.recordType === UserRecordType.IssueTracking) {
      return false;
    }
    // For issue tracking, only allow edit if user is creator or admin/super-admin
    const hasEditPermission =
      this.createdByCurrentUser(row) ||
      (this.recordType === UserRecordType.IssueTracking && (this.isAdmin || this.isSuperAdmin));
    if (!hasEditPermission) {
      return false;
    }
    // If highlightActiveRows is not enabled for issue tracking, also require the row to be active to enable editing
    if (!this.highlightActiveRows || this.recordType !== UserRecordType.IssueTracking) {
      return true;
    }
    // If highlightActiveRows is enabled, only allow edit if the row is active.
    return this.isActiveHighlightRow(row);
  }

  // Helper to determine if a row is a sub-row (used for issue tracking child rows) so we can disable actions on those rows
  private isSubRow(row: T): boolean {
    return !!row['__isSubRow'];
  }

  createdByCurrentUser(row: T): boolean {
    const createdBy = row['createdBy'];
    return createdBy === this.currentUserLocalAccountId;
  }

  getIssueTrackingOnHoldStatus(row: T): {
    certification: boolean;
    endorsement: boolean;
    payment: boolean;
  } {
    const certification = !!row['certificationRestricted'];
    const endorsement = !!row['endorsementRestricted'];
    const payment = !!row['paymentRestricted'];

    return { certification, endorsement, payment };
  }

  getRestrictedIssueTypes(row: T): string[] {
    const { certification, endorsement, payment } = this.getIssueTrackingOnHoldStatus(row);
    const restrictedTypes: string[] = [];

    if (certification) restrictedTypes.push('Certification');
    if (endorsement) restrictedTypes.push('Endorsement');
    if (payment) restrictedTypes.push('Payment');

    return restrictedTypes;
  }

  getRestrictedIssueTypesText(row: T): string {
    const restrictedTypes = this.getRestrictedIssueTypes(row);
    return restrictedTypes.length > 0 ? restrictedTypes.join(', ') : '-';
  }

  get commentsData(): Comment[] {
    return this.data as Comment[];
  }

  get isTasks(): boolean {
    return this.title === 'Tasks';
  }

  get isEmployees(): boolean {
    return this.title === 'Employees';
  }

  get isMessages(): boolean {
    return this.title === 'Messages';
  }

  get isDocuments(): boolean {
    return this.title === 'Documents';
  }

  get isInternalComments(): boolean {
    return this.title === 'Internal Comments For SEAA use only';
  }

  ngOnInit(): void {
    this.currentUserLocalAccountId = this.msalService.instance.getActiveAccount()?.localAccountId ?? '';
    this.programPartnerId = this.route.snapshot.paramMap.get('id')!;
    this.updateDisplayData();
    this.checkUserPermissions(); // Check user permissions on init to set admin or super admin flags
    this.commentsService.refreshComments$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: { action: string; comment?: Comment }) => {
        this.addNewCommentClick.emit({ action: payload.action, comment: payload.comment });
      });
  }

  ngOnChanges(): void {
    this.updateDisplayData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateDisplayData(): void {
    const sliced =
      this.paginated || this.scrollable || this.maxInitialRecords === 0
        ? [...this.data]
        : this.data.slice(0, this.maxInitialRecords);

    if (this.highlightActiveRows) {
      const activeScore = (row: T) => (this.isActiveHighlightRow(row) ? 1 : 0);
      sliced.sort((a, b) => activeScore(b) - activeScore(a));
    }

    // Remap custom subCellsKey to 'subCells' so cfi-mat-table always finds it
    const newDisplayData =
      this.subCellsKey === 'subCells'
        ? sliced
        : sliced.map((row) => {
            const { [this.subCellsKey]: subCells, ...rest } = row as Record<string, unknown>;
            return { ...rest, subCells } as T;
          });

    // Mark child rows inside subCells so row actions render disabled for expanded subrows in issue tracking
    this.displayData = newDisplayData.map((row) => this.markSubCellsAsChildRows(row));
    this.dataVersion++; //increment version
    this.tableUpdated = true;

    if (this.expandable) {
      this.displayedColumns = this.columnDefinitions.map((col) => col.name);
    }

    // Use setTimeout to ensure change detection runs in the next cycle
    setTimeout(() => {
      this.cdr.detectChanges();
      // Reset the flag after a short delay
      setTimeout(() => {
        this.tableUpdated = false;
        this.cdr.detectChanges();
      }, 100);
    }, 0);
  }

  private markSubCellsAsChildRows(row: T): T {
    const expandableRow = row as T & Partial<ExpandableRow>;
    if (!Array.isArray(expandableRow.subCells)) {
      return row;
    }

    return {
      ...row,
      subCells: expandableRow.subCells.map((subCell) => ({
        ...(subCell as Record<string, unknown>),
        __isSubRow: true
      }))
    } as T;
  }

  onViewMoreClick(): void {
    this.viewMoreClick.emit();
  }

  getPendingTasks(data: T[]): number {
    if (data.length === 0) {
      return 0;
    } else {
      return data.filter((task) => redStatuses.includes(task['taskStatus'] as ProviderInfoStatus)).length;
    }
  }

  onAddNewButtonClick(issue?: IssueHistory): void {
    if (issue && this.recordType === UserRecordType.IssueTracking) {
      this.editIssueClick.emit(issue);
      return;
    }

    if (this.isInternalComments) {
      this.isAddComment = true;
    } else {
      this.addNewButtonClick.emit();
    }
  }

  getStatusClass(status: ProviderInfoStatus | string): string {
    if (redStatuses.includes(status as ProviderInfoStatus) || taskRedStatuses.has(status as TaskInfoStatus)) {
      return 'red-text';
    } else if (greenStatuses.includes(status as ProviderInfoStatus)) {
      return 'success-text';
    } else {
      return '';
    }
  }

  getViewToggleLabel(): string {
    // For Messages, always show "View Messages" (it navigates, doesn't toggle)
    if (this.isMessages) {
      return 'View Messages';
    }
    return this.label;
  }

  onSortChange(event: { active: string; direction: SortDirection }): void {
    this.cbSort.emit(event);
  }

  onExpandedElementChange(element: T | null): void {
    this.expandedElement = element;

    // Update the expanded property on the task
    if (element) {
      const expandableElement = element as T & Partial<ExpandableRow>;
      expandableElement.expanded = true;
    } else {
      // Reset expanded state on all tasks
      this.data.forEach((item: T) => {
        const expandableItem = item as T & Partial<ExpandableRow>;
        expandableItem.expanded = false;
      });
    }
    this.cdr.detectChanges();
  }

  onCancelAddComment(): void {
    this.isAddComment = false;
    this.commentBody = '';
  }

  async onSaveComment(): Promise<void> {
    if (!this.commentBody?.trim().length) {
      return;
    }

    this.spinnerService.showSpinner();

    try {
      const payload: Comment = {
        body: this.commentBody.trim(),
        programPartnerId: this.programPartnerId ?? '',
        commentType: this.isInternalComments ? this.commentType.SEAAUseOnly : this.commentType.Account
      };

      const response: Comment = await firstValueFrom(this.commentsService.saveComment(payload));
      if (!response) {
        const errMsg = 'Failed to save comment.';
        this.toastMessageService.showToast(ToastMessageType.Error, errMsg);
        this.appInsightsService.log(new Error(errMsg), SeverityLevel.error);
        return;
      }

      this.toastMessageService.showToast(ToastMessageType.Success, 'Comment saved successfully!');
      this.commentBody = '';
      this.isAddComment = false;
      this.addNewCommentClick.emit({ action: 'save', comment: response });
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      const apiMessage = err?.error?.message || 'Failed to save comment.';
      this.appInsightsService.log(new Error(apiMessage), SeverityLevel.error);
      this.toastMessageService.showToast(ToastMessageType.Error, apiMessage);
    } finally {
      this.spinnerService.hideSpinner();
    }
  }

  onStudentNameClick(details: { studentId: string; studentName: string }): void {
    this.studentClick.emit(details);
  }

  onMessageSent(): void {
    this.messageSent.emit();
  }

  /** Check user group and set flags for admin and super-admin. */
  private async checkUserPermissions(): Promise<void> {
    await Promise.all([
      this.checkUserGroup(EntraUserGroup.GlobalAdmin, 'isSuperAdmin'),
      this.checkUserGroup(EntraUserGroup.Admin, 'isAdmin')
    ]);
  }

  /** Generic function to check if user belongs to a group and set the corresponding flag. */
  private async checkUserGroup(
    group: EntraUserGroup,
    flagName: keyof Pick<UserRecordTableComponent, 'isAdmin' | 'isSuperAdmin'>
  ): Promise<void> {
    try {
      this[flagName] = await this.authService.userHasGroup(group);
    } catch (error) {
      this[flagName] = false;
      this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)), SeverityLevel.error);
    }
  }
}
